import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  matchCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  matchActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
  },
  fieldRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    minWidth: 160,
  },
  fieldValue: {
    flex: 1,
    textAlign: "right",
    wordBreak: "break-word",
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  actionsBar: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
});

function getIdentityRiskLabel(identityRisk) {
  if (!identityRisk) {
    return "No snapshot";
  }

  if (identityRisk.level === "high") {
    return `${identityRisk.exactMatchCount} exact match${
      identityRisk.exactMatchCount === 1 ? "" : "es"
    }`;
  }

  if (identityRisk.level === "medium") {
    return `${identityRisk.softMatchCount} soft match${
      identityRisk.softMatchCount === 1 ? "" : "es"
    }`;
  }

  if (identityRisk.level === "pending") {
    return "Scan pending";
  }

  if (identityRisk.level === "scan_failed") {
    return "Scan failed";
  }

  return "Clear";
}

function getIdentityRiskColor(identityRisk) {
  if (!identityRisk) {
    return "default";
  }

  if (identityRisk.level === "high") {
    return "secondary";
  }

  if (identityRisk.level === "medium" || identityRisk.level === "pending") {
    return "primary";
  }

  return "default";
}

function renderField(classes, label, value) {
  return (
    <div className={classes.fieldRow} key={label}>
      <Typography variant="body2" className={classes.fieldLabel}>
        {label}
      </Typography>
      <Typography variant="body2" className={classes.fieldValue}>
        {value || "Not available"}
      </Typography>
    </div>
  );
}

function IdentityReviewPanel(props) {
  const {
    classes,
    openState,
    user,
    reviewUserContext,
    reviewData,
    loading,
    error,
    onClose,
    onRefresh,
    onConfirmReview,
    onDismissMatch,
    onRunIdentityScan,
  } = props;
  const row = reviewUserContext?.row || null;
  const review = reviewData || null;
  const snapshot = review?.snapshot || null;
  const identityRisk = review?.identityRisk || row?.identityRisk || null;
  const passportHref =
    user?.userId && row?.userId
      ? `${basePath}/api/v7/media/darkon/${user.userId}/${row.userId}`
      : null;
  const selfieHref =
    user?.userId && row?.userId
      ? `${basePath}/api/v7/media/sirton/${user.userId}/${row.userId}`
      : null;

  const renderMatchGroup = (title, matches = []) => (
    <div className={classes.section}>
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {title}
      </Typography>
      {matches.length === 0 ? (
        <Typography variant="body2" className={classes.helperText}>
          No matches in this category.
        </Typography>
      ) : (
        matches.map((match) => (
          <div className={classes.matchCard} key={match.snapshotId}>
            <Typography variant="body1">
              {match?.matchedUser?.name || "Unknown player"}
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              User ID: {match?.matchedUser?.userId || match.sourceUserId}
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Status: {match?.matchedUser?.accountStatus || "pending"}
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Match signal: {match.matchType}
              {match.documentNumberLast4 ? `, doc ****${match.documentNumberLast4}` : ""}
            </Typography>
            <div className={classes.matchActions}>
              <Button
                color="primary"
                size="small"
                variant="contained"
                disabled={loading || !snapshot?.snapshotId}
                onClick={() =>
                  onConfirmReview({
                    snapshotId: snapshot.snapshotId,
                    matchSnapshotId: match.snapshotId,
                  })
                }
              >
                Confirm link
              </Button>
              <Button
                size="small"
                disabled={loading || !snapshot?.snapshotId}
                onClick={() =>
                  onDismissMatch({
                    snapshotId: snapshot.snapshotId,
                    dismissedSnapshotId: match.snapshotId,
                  })
                }
              >
                Dismiss
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Dialog open={openState} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Identity Review
        <Typography variant="body2" className={classes.helperText}>
          {row?.name || row?.email || row?.userId || "Selected player"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.chipRow}>
          <Chip
            color={getIdentityRiskColor(identityRisk)}
            label={getIdentityRiskLabel(identityRisk)}
            size="small"
          />
          <Chip
            label={`Review: ${review?.snapshot?.reviewStatus || row?.identityRisk?.reviewStatus || "pending"}`}
            size="small"
          />
          <Chip
            label={`Scan: ${review?.snapshot?.extractionStatus || row?.identityRisk?.extractionStatus || "unknown"}`}
            size="small"
          />
          <Chip
            label={`Trust factor: ${row?.profileTrustFactor != null ? Number(row.profileTrustFactor).toFixed(2) : "—"}/10`}
            size="small"
          />
          <Chip
            label={`Tier: ${row?.completedOnboardingTier ?? 0}`}
            size="small"
          />
        </div>

        <Typography variant="body2" className={classes.helperText} paragraph>
          Run the scan only after confirming the uploaded file is a passport or personal ID.
        </Typography>

        <div className={classes.actionsBar}>
          <Button onClick={onRefresh} disabled={loading}>
            Refresh
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={loading || !snapshot?.snapshotId}
            onClick={() =>
              onConfirmReview({
                snapshotId: snapshot.snapshotId,
                matchSnapshotId: null,
              })
            }
          >
            Confirm review
          </Button>
          <Button
            disabled={loading || !snapshot?.snapshotId}
            onClick={() =>
              onRunIdentityScan({ snapshotId: snapshot.snapshotId })
            }
          >
            Run ID Scan
          </Button>
        </div>

        {loading ? (
          <Typography variant="body2" className={classes.helperText}>
            Loading review data...
          </Typography>
        ) : null}

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {!loading && !review ? (
          <Typography variant="body2" className={classes.helperText}>
            No identity snapshot is available for this player yet.
          </Typography>
        ) : null}

        {review ? (
          <>
            <div className={classes.section}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Snapshot Details
              </Typography>
              {renderField(classes, "Snapshot ID", snapshot?.snapshotId)}
              {renderField(classes, "Document type", snapshot?.sourceDocumentType)}
              {renderField(classes, "Normalized name", snapshot?.normalizedName)}
              {renderField(classes, "Country", snapshot?.normalizedCountry)}
              {renderField(
                classes,
                "Date of birth",
                snapshot?.normalizedDateOfBirth
              )}
              {renderField(
                classes,
                "Document last 4",
                snapshot?.documentNumberLast4
              )}
            </div>

            <Divider />

            <div className={classes.section}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Evidence
              </Typography>
              <div className={classes.matchActions}>
                {row?.hasPassport && passportHref ? (
                  <Link href={passportHref} target="_blank" rel="noreferrer">
                    Open uploaded document
                  </Link>
                ) : (
                  <Typography variant="body2" className={classes.helperText}>
                    No passport/document uploaded
                  </Typography>
                )}
                {row?.hasSelfieVideo && selfieHref ? (
                  <Link href={selfieHref} target="_blank" rel="noreferrer">
                    Open selfie video
                  </Link>
                ) : null}
              </div>
            </div>

            <Divider />

            {renderMatchGroup("Exact Matches", review.exactMatches || [])}
            {renderMatchGroup("Soft Matches", review.softMatches || [])}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

IdentityReviewPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirmReview: PropTypes.func.isRequired,
  onDismissMatch: PropTypes.func.isRequired,
  onRunIdentityScan: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  openState: PropTypes.bool,
  reviewData: PropTypes.object,
  reviewUserContext: PropTypes.object,
  user: PropTypes.object,
};

IdentityReviewPanel.defaultProps = {
  error: "",
  loading: false,
  openState: false,
  reviewData: null,
  reviewUserContext: null,
  user: null,
};

export default withStyles(styles)(IdentityReviewPanel);
